import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth, API } from '../App';
import { t } from '../i18n';
import { toast } from 'sonner';
import { UploadCloud, Trash2, FileText, Loader2, CheckCircle2, AlertTriangle, CloudDownload, FolderPlus, ScanText } from 'lucide-react';
import Modal from '../components/Modal';
import DocumentDetailModal from '../components/DocumentDetailModal';
import StorageSyncModal from '../components/StorageSyncModal';

export default function DocManagementPage() {
  const { lang } = useAuth();
  const [docs, setDocs] = useState([]);
  const [upload, setUpload] = useState(false);

  const [selId, setSelId] = useState(null);
  const [storageSync, setStorageSync] = useState(false);
  const [newDept, setNewDept] = useState(false);
  const [departments, setDepartments] = useState(['HR', 'Procurement', 'Finance', 'Policy']);

  const load = () => axios.get(`${API}/documents`).then(r => setDocs(r.data));
  const loadDepts = () => axios.get(`${API}/departments`).then(r => setDepartments(r.data.departments)).catch(() => {});
  useEffect(() => { load(); loadDepts(); }, []);

  const remove = async (e, d) => {
    e.stopPropagation();
    if (!confirm(`Delete ${d.name}? Cached answers referencing it will be invalidated.`)) return;
    try {
      await axios.delete(`${API}/documents/${d.id}`);
      toast.success('Deleted · cache invalidated · FAQs marked for re-review');
      load();
    } catch {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="fade-up" data-testid="doc-mgmt-page">
      <div className="flex justify-between items-end mb-6">
        <div>
          <div className="data-label accent-line text-[#DDA079] mb-3">{t(lang, 'nav.admin_documents')}</div>
          <h1 className="font-headings text-4xl text-white">Document Management</h1>
          <p className="text-slate-400 mt-2 max-w-2xl">Upload PDFs — text is extracted, smart-chunked, and auto-summarised. Or paste content manually. Click any document to view its full contents.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="open-new-dept"
            onClick={() => setNewDept(true)}
            className="btn-secondary flex items-center gap-2"
          >
            <FolderPlus size={14} /> New department
          </button>
          <button
            data-testid="open-storage-sync"
            onClick={() => setStorageSync(true)}
            className="btn-secondary flex items-center gap-2"
          >
            <CloudDownload size={14} /> Sync from storage
          </button>
          <button
            data-testid="open-upload"
            onClick={() => setUpload(true)}
            className="btn-primary flex items-center gap-2"
          >
            <UploadCloud size={14} /> Upload document
          </button>
        </div>
      </div>

      <div className="grid gap-2">
        {docs.map(d => (
          <button
            key={d.id}
            data-testid={`mgmt-doc-${d.id}`}
            onClick={() => setSelId(d.id)}
            className="v-card v-card-hover p-4 flex items-start justify-between gap-4 text-left"
          >
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <FileText size={13} className="text-[#C17F59]" />
                <span className="text-white text-[14px]">{d.name}</span>
                <span className="v-badge cost">{d.department}</span>
                <span className="v-badge cost">{d.type}</span>
                <span className="v-badge cost">v{d.version}</span>
                {d.language !== 'en' && <span className="v-badge conf">{d.language.toUpperCase()}</span>}
                {d.status === 'processing' && <span className="v-badge conf" data-testid={`processing-${d.id}`}><Loader2 size={9} className="inline mr-0.5 animate-spin" />PROCESSING</span>}
                {d.status === 'failed' && <span className="v-badge" style={{ color: '#f87171', borderColor: 'rgba(248,113,113,0.4)' }}>FAILED</span>}
                {d.metadata?.ingested_via === 'pdf_upload' && <span className="v-badge ai">PDF</span>}
                {d.source === 'azure_blob' && <span className="v-badge ai">BLOB · {d.folder}</span>}
                {d.ocr?.engine && <span className="v-badge conf"><ScanText size={9} className="inline mr-0.5" />OCR</span>}
              </div>
              <div className="text-[11px] text-slate-500 font-mono">
                {d.pages} pages · uploaded {new Date(d.uploaded_at).toLocaleDateString('en-IN')} by {d.uploaded_by}
              </div>
            </div>
            <span
              role="button"
              tabIndex={0}
              data-testid={`del-${d.id}`}
              onClick={(e) => remove(e, d)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') remove(e, d); }}
              className="btn-danger flex items-center gap-1 cursor-pointer select-none"
            >
              <Trash2 size={12} /> {t(lang, 'common.delete')}
            </span>
          </button>
        ))}
      </div>

      {/* Document detail (portal) */}
      {selId && <DocumentDetailModal docId={selId} onClose={() => setSelId(null)} />}

      {/* Storage sync (portal) */}
      {storageSync && (
        <StorageSyncModal
          onClose={() => setStorageSync(false)}
          onSynced={load}
        />
      )}

      {/* New department (portal) */}
      {newDept && (
        <NewDepartmentModal
          onClose={() => setNewDept(false)}
          onCreated={() => { setNewDept(false); loadDepts(); }}
        />
      )}

      {/* Upload modal (portal) */}
      {upload && (
        <Modal onClose={() => setUpload(false)} testid="upload-modal" widthClass="max-w-2xl">
          <div className="px-8 pt-7 pb-4 border-b border-white/10">
            <div className="data-label accent-line text-[#DDA079] mb-2">New document</div>
            <h2 className="font-headings text-2xl text-white">Upload &amp; ingest</h2>
          </div>

          <div className="flex-1 overflow-y-auto px-8 py-6">
            <PDFUploadPanel onDone={() => { setUpload(false); load(); }} cancel={() => setUpload(false)} lang={lang} departments={departments} />
          </div>
        </Modal>
      )}
    </div>
  );
}

function NewDepartmentModal({ onClose, onCreated }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  const create = async () => {
    if (!name.trim()) { toast.error('Enter a department name'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/departments`, { name: name.trim() });
      toast.success(`Department "${name.trim()}" created`);
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create department');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose} testid="new-dept-modal" widthClass="max-w-md">
      <div className="px-8 pt-7 pb-4 border-b border-white/10">
        <div className="data-label accent-line text-[#DDA079] mb-2">Governance</div>
        <h2 className="font-headings text-2xl text-white">Create department</h2>
        <p className="text-slate-400 text-[12px] mt-1">Starts empty — documents can be uploaded or synced from storage into it immediately.</p>
      </div>
      <div className="px-8 py-6">
        <label className="data-label block mb-2">Department name</label>
        <input
          data-testid="new-dept-name"
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') create(); }}
          className="v-input"
          placeholder="e.g. Legal"
          autoFocus
        />
      </div>
      <div className="px-8 py-4 border-t border-white/10 flex justify-end gap-2">
        <button onClick={onClose} className="btn-secondary">Cancel</button>
        <button data-testid="new-dept-submit" onClick={create} disabled={busy} className="btn-primary flex items-center gap-2">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <FolderPlus size={13} />} Create
        </button>
      </div>
    </Modal>
  );
}

function PDFUploadPanel({ onDone, cancel, lang, departments }) {
  const [file, setFile] = useState(null);
  const [name, setName] = useState('');
  const [department, setDepartment] = useState('HR');
  const [docType, setDocType] = useState('HR Policy');
  const [language, setLanguage] = useState('en');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.pdf')) { toast.error('Only .pdf files accepted'); return; }
    if (f.size > 25 * 1024 * 1024) { toast.error('File too large (max 25 MB)'); return; }
    setFile(f);
    if (!name) setName(f.name.replace(/\.pdf$/i, ''));
  };

  const submit = async () => {
    if (!file) { toast.error('Choose a PDF first'); return; }
    setBusy(true); setResult(null);
    const fd = new FormData();
    fd.append('file', file);
    if (name) fd.append('name', name);
    fd.append('department', department);
    fd.append('doc_type', docType);
    fd.append('language', language);
    try {
      const { data } = await axios.post(`${API}/documents/upload-pdf`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 90000,
      });
      setResult(data);
      toast.success('Upload received — extraction running in background');
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'PDF upload failed');
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    return (
      <div className="space-y-4 fade-up" data-testid="pdf-upload-success">
        <div className="flex items-start gap-3 border border-emerald-500/30 bg-emerald-500/[0.05] p-4 rounded-sm">
          <CheckCircle2 size={18} className="text-emerald-400 mt-0.5 flex-shrink-0" />
          <div>
            <div className="text-white text-[14px] font-medium">{result.name} — upload received</div>
            <div className="text-[12px] text-slate-400 mt-1">
              AI extraction (Gemini Vision) is running in the background — the document will show
              a PROCESSING badge in the list and become fully searchable in a few minutes.
            </div>
          </div>
        </div>
        {result.ocr_used && (
          <div className="flex items-start gap-3 border border-sky-500/30 bg-sky-500/[0.05] p-4 rounded-sm" data-testid="ocr-used-banner">
            <ScanText size={18} className="text-sky-400 mt-0.5 flex-shrink-0" />
            <div className="text-[13px] text-sky-200">
              Scanned document detected — text was extracted via <strong>{result.ocr_engine}</strong> OCR
              {result.language !== 'en' && <> · language detected: <strong>{result.language.toUpperCase()}</strong></>}.
            </div>
          </div>
        )}
        {!result.has_text_layer && !result.ocr_used && (
          <div className="flex items-start gap-3 border border-amber-500/30 bg-amber-500/[0.05] p-4 rounded-sm" data-testid="scanned-warn">
            <AlertTriangle size={18} className="text-amber-400 mt-0.5 flex-shrink-0" />
            <div className="text-[13px] text-amber-200">
              This appears to be a scanned/image-only PDF (no text layer). The Summary Lookup route will not work until you add a manual summary.
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-4">
          <button onClick={() => { setResult(null); setFile(null); setName(''); if (fileRef.current) fileRef.current.value = ''; }} className="btn-secondary">
            Upload another
          </button>
          <button onClick={onDone} className="btn-primary">Done</button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div
        data-testid="pdf-dropzone"
        onClick={() => fileRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); }}
        onDrop={(e) => {
          e.preventDefault();
          const f = e.dataTransfer.files?.[0];
          if (f) onPick({ target: { files: [f] } });
        }}
        className="border-2 border-dashed border-white/15 hover:border-[#C17F59] cursor-pointer p-8 text-center rounded-sm transition-colors"
      >
        <UploadCloud size={32} className="mx-auto text-slate-500 mb-3" />
        {file ? (
          <>
            <div className="text-white text-[14px]" data-testid="pdf-selected-name">{file.name}</div>
            <div className="text-[11px] text-slate-500 font-mono mt-1">{(file.size / 1024).toFixed(1)} KB · click to change</div>
          </>
        ) : (
          <>
            <div className="text-slate-300 text-[14px]">Drop a PDF here or click to browse</div>
            <div className="text-[11px] text-slate-500 mt-1">Max 25 MB · text-layer PDFs are auto-chunked + auto-summarised</div>
          </>
        )}
        <input
          ref={fileRef}
          data-testid="pdf-file-input"
          type="file"
          accept="application/pdf"
          className="hidden"
          onChange={onPick}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Display name (optional)">
          <input data-testid="pdf-name" value={name} onChange={e => setName(e.target.value)} className="v-input" placeholder="Defaults to the filename" />
        </Field>
        <Field label="Document type">
          <input data-testid="pdf-type" value={docType} onChange={e => setDocType(e.target.value)} className="v-input" />
        </Field>
        <Field label="Department">
          <select data-testid="pdf-dept" value={department} onChange={e => setDepartment(e.target.value)} className="v-input">
            {departments.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </Field>
        <Field label="Language">
          <select data-testid="pdf-lang" value={language} onChange={e => setLanguage(e.target.value)} className="v-input">
            <option value="en">English</option>
            <option value="hi">Hindi</option>
            <option value="te">Telugu</option>
          </select>
        </Field>
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <button onClick={cancel} className="btn-secondary">{t(lang, 'common.cancel')}</button>
        <button data-testid="pdf-upload-submit" onClick={submit} disabled={!file || busy} className="btn-primary flex items-center gap-2">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <UploadCloud size={13} />}
          {busy ? 'Extracting · chunking · summarising…' : 'Upload & Ingest PDF'}
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="data-label block mb-1.5">{label}</label>
      {children}
    </div>
  );
}
